import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react'
import { Icon } from '@iconify/react'
import {
  RASPADINHA_PDF_ACCEPT,
  formatRaspadinhaPdfFileSize,
  validateRaspadinhaPdfFile,
} from '@/lib/raspadinha-campaigns/pdf-upload'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import styles from './RaspadinhaPdfUploadField.module.css'

type RaspadinhaPdfUploadFieldProps = {
  label: string
  hint: string
  pdfUrl: string
  pdfName: string
  onPdfChange: (url: string, name: string, file?: File | null) => void
  disabled?: boolean
}

export function RaspadinhaPdfUploadField({
  label,
  hint,
  pdfUrl,
  pdfName,
  onPdfChange,
  disabled = false,
}: RaspadinhaPdfUploadFieldProps) {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isReading, setIsReading] = useState(false)
  const [selectedSizeBytes, setSelectedSizeBytes] = useState<number | null>(null)
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null)

  const hasPdf = Boolean(localPreviewUrl ?? pdfUrl)
  const displayUrl = localPreviewUrl ?? pdfUrl

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl)
      }
    }
  }, [localPreviewUrl])

  useEffect(() => {
    if (!pdfUrl && !localPreviewUrl) {
      setSelectedSizeBytes(null)
    }
  }, [localPreviewUrl, pdfUrl])

  const displayName = pdfName || 'Documento.pdf'

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    event.target.value = ''

    if (!file) {
      return
    }

    const validationError = validateRaspadinhaPdfFile(file, label)
    if (validationError) {
      setErrorMessage(validationError)
      return
    }

    setErrorMessage(null)
    setIsReading(true)

    try {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl)
      }
      const objectUrl = URL.createObjectURL(file)
      setLocalPreviewUrl(objectUrl)
      setSelectedSizeBytes(file.size)
      onPdfChange(objectUrl, file.name, file)
    } catch {
      setErrorMessage('Não foi possível carregar o PDF. Tente outro arquivo.')
    } finally {
      setIsReading(false)
    }
  }

  const handleRemove = () => {
    setErrorMessage(null)
    setSelectedSizeBytes(null)
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl)
      setLocalPreviewUrl(null)
    }
    onPdfChange('', '', null)
  }

  const isDisabled = disabled || isReading

  return (
    <div className={editStyles.editMediaBlock}>
      <label className={editStyles.editLabel} htmlFor={inputId}>
        {label}
        <span className={editStyles.editLabelHint}>{hint}</span>
      </label>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={RASPADINHA_PDF_ACCEPT}
        className={editStyles.fileInput}
        onChange={handleFileChange}
        disabled={isDisabled}
      />
      <button
        type="button"
        className={`${editStyles.editUploadZone} ${hasPdf ? editStyles.editUploadZoneHasFile : ''}`}
        onClick={() => inputRef.current?.click()}
        disabled={isDisabled}
      >
        {hasPdf ? (
          <div className={editStyles.editPdfPreview}>
            <Icon icon="mdi:file-pdf-box" style={{ fontSize: 48, color: '#dc2626' }} aria-hidden />
            <span className={editStyles.editPdfFileName}>{displayName}</span>
            {selectedSizeBytes ? (
              <span className={editStyles.editPdfFileSize}>
                {formatRaspadinhaPdfFileSize(selectedSizeBytes)}
              </span>
            ) : null}
            <a
              href={displayUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={editStyles.editPdfViewButton}
              onClick={(event) => event.stopPropagation()}
            >
              <Icon icon="mdi:open-in-new" style={{ fontSize: 16 }} aria-hidden />
              Visualizar PDF
            </a>
          </div>
        ) : (
          <>
            <Icon
              icon="mdi:file-pdf-box-outline"
              style={{ fontSize: 28, color: 'var(--color-text-muted)' }}
              aria-hidden
            />
            <span className={editStyles.editUploadHint}>
              {isReading ? 'Carregando PDF…' : 'PDF, até 10 MB'}
            </span>
            <span className={editStyles.editUploadButton}>Escolher arquivo</span>
          </>
        )}
      </button>
      {hasPdf && !disabled ? (
        <button type="button" className={styles.removeButton} onClick={handleRemove}>
          Remover PDF
        </button>
      ) : null}
      {errorMessage ? (
        <p className={styles.errorMessage} role="alert">
          {errorMessage}
        </p>
      ) : null}
    </div>
  )
}
